// NotificationContext.jsx
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Notification from '.';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const timeoutsRef = useRef(new Map()); // id -> timeoutId

  const scheduleAutoClose = useCallback((id, delay) => {
    if (timeoutsRef.current.has(id)) {
      clearTimeout(timeoutsRef.current.get(id));
      timeoutsRef.current.delete(id);
    }
    const tid = setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      timeoutsRef.current.delete(id);
    }, delay);
    timeoutsRef.current.set(id, tid);
  }, []);

  const closeNow = useCallback((id) => {
    if (timeoutsRef.current.has(id)) {
      clearTimeout(timeoutsRef.current.get(id));
      timeoutsRef.current.delete(id);
    }
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // show(message, type, delay?, title?, date?, options?)
  const showNotification = useCallback(
    (message, type, delay = 15000, title = 'Aviso', date = new Date().toLocaleTimeString(), options = {}) => {
      const id = Date.now() + Math.random(); // id único
      const sticky = !!options.sticky || type === 'loading'; // loading fica sticky por padrão

      const newNotification = { id, message, type, title, date, delay };

      setNotifications((prev) => [...prev, newNotification]);

      if (!sticky) scheduleAutoClose(id, delay);

      return id;
    },
    [scheduleAutoClose]
  );

  // update(id, patch)
  const updateNotification = useCallback((id, patch = {}) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, ...patch, id } : n))
    );
    // reprograma autohide se veio delay e não for sticky
    if (typeof patch.delay === 'number' && patch.delay >= 0 && patch.sticky !== true && patch.type !== 'loading') {
      scheduleAutoClose(id, patch.delay);
    }
    // se pediu pra virar sticky ou virou loading, cancela timeout
    if (patch.sticky === true || patch.type === 'loading') {
      if (timeoutsRef.current.has(id)) {
        clearTimeout(timeoutsRef.current.get(id));
        timeoutsRef.current.delete(id);
      }
    }
  }, [scheduleAutoClose]);

  // expõe função principal com helpers acoplados (retrocompatível)
  showNotification.update = updateNotification;
  showNotification.close = closeNow;

  return (
    <NotificationContext.Provider value={showNotification}>
      {children}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          margin: '10px',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '10px',
        }}
      >
        {notifications.map((n) => (
          <Notification
            key={n.id}
            {...n}
            onClosed={() => closeNow(n.id)} // remove do array quando usuário fecha manualmente
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within a NotificationProvider');
  return context; // é a função showNotification, com .update e .close
};
